import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Collapse,
  Button,
  Grid,
  Stack,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ReplayIcon from "@mui/icons-material/Replay";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import PHASES from "./workflowData";

export default function InteractiveWorkflow({ articles = [] }) {
  const [expandedStep, setExpandedStep] = useState(null);
  const navigate = useNavigate();

  const articlesByStep = useMemo(() => {
    const map = {};
    for (const article of articles) {
      if (!article.workflow_steps?.length) continue;
      for (const step of article.workflow_steps) {
        if (!map[step]) map[step] = [];
        map[step].push(article);
      }
    }
    return map;
  }, [articles]);

  const toggleStep = useCallback(
    (stepNum) => setExpandedStep((prev) => (prev === stepNum ? null : stepNum)),
    [],
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Your Coaching Workflow
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Click any step to see related features, guides, and direct links.
      </Typography>

      <Grid container spacing={2}>
        {PHASES.map((phase) => (
          <Grid item xs={12} md={6} lg={3} key={phase.id}>
            <Card
              variant="outlined"
              sx={{
                borderTop: `4px solid ${phase.color}`,
                borderRadius: 2,
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, textAlign: "left" }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Chip
                    label={`Phase ${phase.number}`}
                    size="small"
                    sx={{
                      bgcolor: phase.color,
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                    }}
                  />
                  {phase.isRecurring && (
                    <Chip
                      icon={<ReplayIcon sx={{ fontSize: 14, color: "inherit !important" }} />}
                      label="Recurring"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.65rem", height: 22 }}
                    />
                  )}
                </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {phase.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  {phase.tagline}
                </Typography>

                {phase.audio && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{
                      mb: 1.5,
                      p: 0.75,
                      borderRadius: 1.5,
                      bgcolor: `${phase.color}08`,
                      border: `1px solid ${phase.color}20`,
                    }}
                  >
                    <HeadphonesIcon sx={{ fontSize: 16, color: phase.color, flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: phase.color, whiteSpace: "nowrap", fontSize: "0.65rem" }}
                    >
                      Listen
                    </Typography>
                    <Box
                      component="audio"
                      controls
                      preload="none"
                      src={phase.audio}
                      sx={{
                        height: 28,
                        flex: 1,
                        minWidth: 0,
                        "&::-webkit-media-controls-panel": { bgcolor: "transparent" },
                      }}
                    />
                  </Stack>
                )}

                <Stack spacing={0.5}>
                  {phase.steps.map((step) => {
                    const isExpanded = expandedStep === step.number;
                    const stepArticles = articlesByStep[step.number] || [];

                    return (
                      <Box key={step.number}>
                        <Box
                          onClick={() => toggleStep(step.number)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: isExpanded ? `${phase.color}10` : "transparent",
                            border: isExpanded ? `1px solid ${phase.color}30` : "1px solid transparent",
                            "&:hover": { bgcolor: `${phase.color}08` },
                            transition: "all 0.15s",
                          }}
                        >
                          <Chip
                            label={step.number}
                            size="small"
                            sx={{
                              height: 24,
                              width: 24,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              bgcolor: isExpanded ? phase.color : "action.hover",
                              color: isExpanded ? "white" : "text.primary",
                              "& .MuiChip-label": { px: 0 },
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, fontWeight: isExpanded ? 600 : 400, fontSize: "0.8rem" }}
                          >
                            {step.title}
                          </Typography>
                          {step.clientSide && (
                            <PhoneIphoneIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                          )}
                          {step.automatic && (
                            <AutoModeIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                          )}
                          <IconButton size="small" sx={{ p: 0 }}>
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 18,
                                transform: isExpanded ? "rotate(180deg)" : "none",
                                transition: "transform 0.2s",
                              }}
                            />
                          </IconButton>
                        </Box>

                        <Collapse in={isExpanded}>
                          <Box sx={{ pl: 4.5, pr: 1, pb: 1.5, pt: 0.5, textAlign: "left", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.5 }}>
                              {step.description}
                            </Typography>

                            {step.clientSide && (
                              <Chip
                                icon={<PhoneIphoneIcon sx={{ fontSize: 12 }} />}
                                label="Happens on client's mobile app"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.65rem", height: 22, mb: 1 }}
                              />
                            )}
                            {step.automatic && (
                              <Chip
                                icon={<AutoModeIcon sx={{ fontSize: 12 }} />}
                                label="Happens automatically"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.65rem", height: 22, mb: 1 }}
                              />
                            )}

                            {stepArticles.length > 0 && (
                              <Stack spacing={0.5} sx={{ mb: 1 }} alignItems="flex-start">
                                {stepArticles.map((a) => (
                                  <Button
                                    key={a.slug}
                                    size="small"
                                    startIcon={<MenuBookIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => navigate(`/home/learn/${a.slug}`)}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-start",
                                      textTransform: "none",
                                      fontSize: "0.75rem",
                                      py: 0.25,
                                      px: 1,
                                      fontWeight: 500,
                                      textAlign: "left",
                                    }}
                                  >
                                    {a.title}
                                  </Button>
                                ))}
                              </Stack>
                            )}

                            {step.appRoutes?.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-start">
                                {step.appRoutes.map((route) => (
                                  <Button
                                    key={route}
                                    size="small"
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                                    onClick={() => navigate(route)}
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "0.65rem",
                                      py: 0.25,
                                      px: 1,
                                      borderRadius: 1,
                                    }}
                                  >
                                    Go to {route.split("/").pop()}
                                  </Button>
                                ))}
                              </Stack>
                            )}

                            {step.isCycleEnd && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 0.75,
                                  borderRadius: 1,
                                  bgcolor: `${phase.color}08`,
                                  border: `1px dashed ${phase.color}40`,
                                }}
                              >
                                <Typography variant="caption" sx={{ fontStyle: "italic", color: phase.color }}>
                                  Cycle repeats back to Step 9 (Receive Plans)
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
